import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';

import type { AppChange } from '@keboola/api-client/developerPortal/types';
import {
  Button,
  EmptyState,
  Modal,
  PageHeader,
  PageLayout,
  Skeleton,
  Table,
} from '@keboola/design';
import { ArrowLeft } from '@keboola/design/icons';

import { QueryError } from '../../../../components/QueryError';
import { COMPONENT_CHANGES_DAYS } from '../../../../features/admin/constants';
import { useAdminApp, useAppChanges } from '../../../../features/admin/queries';
import { AttributeDiff } from '../../../../features/apps/components/AttributeDiff';

export const Route = createFileRoute('/admin/components/$appId/changes')({
  component: AdminComponentChangesPage,
  head: () => ({ meta: [{ title: 'Component changes | Keboola Developer Portal admin' }] }),
});

const formatChangedOn = (createdOn: string): string => new Date(createdOn).toLocaleString();

function AdminComponentChangesPage() {
  const { appId } = Route.useParams();
  const app = useAdminApp(appId);
  const changes = useAppChanges(appId);
  const [shown, setShown] = useState<AppChange | null>(null);

  // apps-api returns an entry per write, including ones that changed nothing — a save with no edits. The
  // original filters those out and so does this: a row with no attributes has no diff to show.
  const withChanges = (changes.data ?? []).filter((change) => change.changes.length > 0);

  return (
    <PageLayout>
      <PageLayout.Header>
        <PageHeader.Detail
          title={app.data?.name ?? appId}
          description={`Changes from the last ${COMPONENT_CHANGES_DAYS} days.`}
          secondaryAction={
            <Button asChild variant="secondary">
              <Link to="/admin/components/$appId/versions" params={{ appId }}>
                Versions and revert
              </Link>
            </Button>
          }
          breadcrumbs={
            <Button asChild variant="inline">
              <Link to="/admin/components/$appId" params={{ appId }}>
                <ArrowLeft />
                Back to the component
              </Link>
            </Button>
          }
        />
      </PageLayout.Header>

      {changes.isLoading && <Skeleton className="tw:h-64" />}

      <QueryError
        isError={changes.isError}
        hasData={changes.data !== undefined}
        error={changes.error}
        onRetry={() => void changes.refetch()}
        loadTitle="Could not load the change log"
        refreshTitle="Could not refresh the change log"
        staleNote="Showing the changes from the last successful load."
        titleAs="h2"
      />

      {changes.data !== undefined && withChanges.length === 0 && (
        <EmptyState variant="compact">
          <EmptyState.Description>
            No changes in the last {COMPONENT_CHANGES_DAYS} days. The window is fixed — apps-api
            keeps no longer history here.
          </EmptyState.Description>
        </EmptyState>
      )}

      {withChanges.length > 0 && (
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Head>Changed on</Table.Head>
              <Table.Head>Changed by</Table.Head>
              <Table.Head>Attributes</Table.Head>
              <Table.Head>
                <span className="tw:sr-only">Actions</span>
              </Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {withChanges.map((change, index) => (
              // - `id` is the app the change belongs to, so it is the same on every row here.
              // - The row position is in the key because two saves by one person inside the same second
              //   are otherwise indistinguishable, and a repeated key makes React reuse the wrong row.
              // oxlint-disable-next-line react/no-array-index-key
              <Table.Row key={`${change.createdOn}-${change.createdBy ?? ''}-${index}`}>
                <Table.Cell>{formatChangedOn(change.createdOn)}</Table.Cell>
                <Table.Cell>{change.createdBy ?? '—'}</Table.Cell>
                <Table.Cell>
                  <span className="tw:text-sm">
                    {change.changes.map((item) => item.attribute).join(', ')}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <div className="tw:flex tw:justify-end">
                    <Button variant="secondary" size="small" onClick={() => setShown(change)}>
                      Show diff
                    </Button>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      <Modal open={shown !== null} onOpenChange={(open) => !open && setShown(null)}>
        <Modal.Content size="large">
          <Modal.Header
            description={
              shown === null
                ? undefined
                : `${formatChangedOn(shown.createdOn)}${shown.createdBy === null ? '' : ` · ${shown.createdBy}`}`
            }
          >
            Changes
          </Modal.Header>
          <Modal.Body>
            <div className="tw:flex tw:flex-col tw:gap-6">
              {shown !== null &&
                shown.changes.map((item) => (
                  <AttributeDiff
                    key={item.attribute}
                    attribute={item.attribute}
                    oldValue={item.oldValue}
                    newValue={item.newValue}
                  />
                ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Modal.Close asChild>
              <Button type="button" variant="secondary">
                Close
              </Button>
            </Modal.Close>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    </PageLayout>
  );
}
